// src/components/brands.js
// Marques — /brands. The account's brands: each one ties its incoming
// mailboxes, its order / tracking API, its support page and its AI agents
// (chatbot, ticket form, tracking page and notifications pick the brand).
// Header → « À vérifier » (an API that calls without its key, a parts source
// without its access key) → tiles (brands, mailboxes, tracking APIs, agents)
// → one card per brand. Search in the URL (?q=).
// API: GET /api/brands/list, POST /api/brands/create, PUT /api/brands/update/{id},
// DELETE /api/brands/delete/{id}, POST /api/brands/associate-email,
// /remove-email-association, /set-vip-email; GET /api/users/user,
// /api/users/user/emails, /api/tickets/embed?brand_id= (copy the form),
// GET /api/ai-assistants (read only: which agent answers for which brand).
// The API key is never shown back (has_api_key → « enregistrée »); the
// support page renders as a link only when http(s).

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Bot, ChevronRight, CodeXml, ExternalLink, Mail, Pencil, Plug, Plus, Search, Star, Tag, Tags, Trash2, TriangleAlert, X,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Field, Kpi, Modal, OverflowMenu, PageHeader, Switch, Toast,
} from '../design/ui';
import { webHref } from '../utils/links';
import {
    agentsByBrand, brandApiState, brandEmails, brandsAttention, brandsSummary, formatCount, formatDay, hasPartsSource,
} from './dataToolsModel';
import './brands.css';

const emptyForm = () => ({
    name: '',
    description: '',
    support_page_url: '',
    tracking_api_config: {
        enabled: true,
        orders_get_url: '',
        orders_tracking_url: '',
        stock_url: '',
        api_key: '',
        parts_api_url: '',
    },
});

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const Brands = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get('q') || '';

    const [user, setUser] = useState(null);
    const [brands, setBrands] = useState(null); // null = loading
    const [loadError, setLoadError] = useState(null);
    const [userEmails, setUserEmails] = useState({ gmail_email: null, outlook_emails: [] });
    const [assistants, setAssistants] = useState(null); // null = unknown (not loaded / refused)
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    // modal = {editing: brand|null, form, error, saving}
    const [modal, setModal] = useState(null);
    // deleteModal = {brand, deleting, error}
    const [deleteModal, setDeleteModal] = useState(null);
    // emailModal = {brandId, pendingEmail, vip, loading, error}
    const [emailModal, setEmailModal] = useState(null);
    // removeEmail = {brand, email}
    const [removeEmailConfirm, setRemoveEmailConfirm] = useState(null);

    const showToast = useCallback((tone, text) => {
        clearTimeout(toastTimer.current);
        setToast({ tone, text });
        if (tone === 'success') toastTimer.current = setTimeout(() => setToast(null), 5000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const fetchBrands = useCallback(async () => {
        try {
            const response = await axios.get('/api/brands/list', { headers: authHeaders() });
            setBrands(response.data || []);
            setLoadError(null);
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands((previous) => previous || []);
            setLoadError(t('brandsPage.fetchError', 'Impossible de charger les marques.'));
        }
    }, [t]);

    useEffect(() => {
        const headers = authHeaders();
        axios.get('/api/users/user', { headers })
            .then((res) => setUser(res.data))
            .catch((err) => console.error('Failed to fetch user:', err));
        axios.get('/api/users/user/emails', { headers })
            .then((res) => setUserEmails(res.data || { gmail_email: null, outlook_emails: [] }))
            .catch((err) => console.error('Failed to fetch user emails:', err));
        axios.get('/api/ai-assistants', { headers })
            .then((res) => setAssistants(Array.isArray(res.data) ? res.data : null))
            .catch(() => setAssistants(null));
        fetchBrands();
    }, [fetchBrands]);

    const canManage = user && ['admin', 'seller', 'eshop'].includes(user.role);

    // Tracking API fields — data-driven so new config fields can't be
    // silently dropped; the only actionable gap is a missing API key.
    const API_FIELDS = [
        { key: 'orders_get_url', label: t('brandsPage.apiOrdersUrl', 'URL des commandes') },
        { key: 'orders_tracking_url', label: t('brandsPage.apiTrackingUrl', 'URL de suivi') },
        { key: 'stock_url', label: t('brandsPage.apiStockUrl', 'URL du stock') },
        { key: 'has_api_key', label: t('brandsPage.apiKey', 'Clé API'), isKey: true },
        { key: 'parts_api_url', label: t('brandsPage.apiPartsUrl', 'URL des pièces détachées'), shownIf: hasPartsSource },
    ];
    const apiFieldsOf = (config) => API_FIELDS.filter((f) => !f.shownIf || f.shownIf(config));

    // ------------------------------------------------------------ CRUD

    const openModal = (brand = null) => {
        if (!canManage) return;
        setModal({
            editing: brand,
            form: brand ? {
                name: brand.name,
                description: brand.description || '',
                support_page_url: brand.support_page_url || '',
                tracking_api_config: {
                    enabled: brand.tracking_api_config?.enabled !== false,
                    orders_get_url: brand.tracking_api_config?.orders_get_url || '',
                    orders_tracking_url: brand.tracking_api_config?.orders_tracking_url || '',
                    stock_url: brand.tracking_api_config?.stock_url || '',
                    api_key: '', // never pre-filled
                    parts_api_url: brand.tracking_api_config?.parts_api_url || '',
                },
            } : emptyForm(),
            error: null,
            saving: false,
        });
    };

    const setForm = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));
    const setApiForm = (patch) => setModal((m) => ({
        ...m,
        form: { ...m.form, tracking_api_config: { ...m.form.tracking_api_config, ...patch } },
        error: null,
    }));

    const submitModal = async (event) => {
        if (event) event.preventDefault();
        const { form, editing } = modal;
        if (!form.name.trim() || !form.description.trim()) {
            setModal((m) => ({ ...m, error: t('brandsPage.requiredFields', 'Le nom et la description sont requis.') }));
            return;
        }
        // Always sent: '' clears the support page (KI-218), the backend checks it is https.
        const data = {
            name: form.name.trim(),
            description: form.description.trim(),
            support_page_url: (form.support_page_url || '').trim(),
        };
        const config = form.tracking_api_config;
        const showParts = hasPartsSource(editing?.tracking_api_config);
        if (config.orders_get_url || config.orders_tracking_url || config.stock_url || config.api_key || showParts) {
            data.tracking_api_config = { enabled: config.enabled !== false };
            if (config.orders_get_url) data.tracking_api_config.orders_get_url = config.orders_get_url;
            if (config.orders_tracking_url) data.tracking_api_config.orders_tracking_url = config.orders_tracking_url;
            // Always sent: the backend keeps an omitted stock URL, '' clears it (KI-215).
            data.tracking_api_config.stock_url = (config.stock_url || '').trim();
            if (config.api_key) data.tracking_api_config.api_key = config.api_key;
            // Sent when the field is shown: '' clears it; omitted, the backend keeps it (KI-218).
            if (showParts) data.tracking_api_config.parts_api_url = (config.parts_api_url || '').trim();
        }
        setModal((m) => ({ ...m, saving: true, error: null }));
        try {
            if (editing) {
                await axios.put(`/api/brands/update/${editing.id}`, data, { headers: authHeaders() });
                showToast('success', t('brandsPage.updated', 'Marque mise à jour.'));
            } else {
                await axios.post('/api/brands/create', data, { headers: authHeaders() });
                showToast('success', t('brandsPage.created', 'Marque créée.'));
            }
            setModal(null);
            fetchBrands();
        } catch (error) {
            console.error('Failed to save brand:', error);
            const detail = error.response?.data?.detail;
            setModal((m) => ({
                ...m, saving: false,
                error: typeof detail === 'string' ? detail : t('brandsPage.saveError', 'Échec de l’enregistrement de la marque.'),
            }));
        }
    };

    const confirmDelete = async () => {
        setDeleteModal((m) => ({ ...m, deleting: true, error: null }));
        try {
            await axios.delete(`/api/brands/delete/${deleteModal.brand.id}`, { headers: authHeaders() });
            setDeleteModal(null);
            showToast('success', t('brandsPage.deleted', 'Marque supprimée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to delete brand:', error);
            const detail = error.response?.data?.detail;
            setDeleteModal(null);
            showToast('danger', typeof detail === 'string' ? detail : t('brandsPage.deleteError', 'Échec de la suppression de la marque.'));
        }
    };

    // ------------------------------------------------------------ emails

    const associateEmail = async (brandId, email, isVip) => {
        setEmailModal((m) => ({ ...m, loading: true, error: null }));
        try {
            await axios.post('/api/brands/associate-email',
                { brand_id: brandId, email, is_vip: isVip },
                { headers: authHeaders() });
            setEmailModal(null);
            showToast('success', t('brandsPage.emailAssociated', 'Boîte mail associée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to associate email:', error);
            const detail = error.response?.data?.detail;
            setEmailModal((m) => ({
                ...m, loading: false,
                error: typeof detail === 'string' ? detail : t('brandsPage.emailAssociateError', 'Échec de l’association de la boîte mail.'),
            }));
        }
    };

    const removeEmail = async (brandId, email) => {
        try {
            await axios.post('/api/brands/remove-email-association',
                { brand_id: brandId, email },
                { headers: authHeaders() });
            showToast('success', t('brandsPage.emailRemoved', 'Association supprimée.'));
            fetchBrands();
        } catch (error) {
            console.error('Failed to remove email association:', error);
            const detail = error.response?.data?.detail;
            showToast('danger', typeof detail === 'string' ? detail : t('brandsPage.emailRemoveError', 'Échec de la suppression de l’association.'));
        } finally {
            setRemoveEmailConfirm(null);
        }
    };

    const toggleVip = async (brandId, email, currentVip) => {
        try {
            await axios.post('/api/brands/set-vip-email',
                { brand_id: brandId, email, is_vip: !currentVip },
                { headers: authHeaders() });
            fetchBrands();
        } catch (error) {
            console.error('Failed to toggle VIP:', error);
            const detail = error.response?.data?.detail;
            showToast('danger', typeof detail === 'string' ? detail : t('brandsPage.vipError', 'Échec du changement de statut VIP.'));
        }
    };

    // ------------------------------------------------------------ embed copy

    const copyTicketEmbed = async (brand) => {
        try {
            const params = brand.id ? `?brand_id=${brand.id}` : '';
            const response = await axios.get(`/api/tickets/embed${params}`, { headers: authHeaders() });
            const code = response.data?.embed_code || response.data;
            await navigator.clipboard.writeText(typeof code === 'string' ? code : JSON.stringify(code));
            showToast('success', t('dataToolsUi.brands.embedCopied', 'Code du formulaire de « {{name}} » copié : collez-le dans votre page support.', { name: brand.name }));
        } catch (error) {
            console.error('Failed to copy embed code:', error);
            showToast('danger', t('brandsPage.embedError', 'Impossible de copier le code du formulaire.'));
        }
    };

    // ------------------------------------------------------------ derived

    const loading = brands === null;
    const list = useMemo(() => brands || [], [brands]);
    const summary = useMemo(() => brandsSummary(list, assistants), [list, assistants]);
    const attention = useMemo(() => brandsAttention(list), [list]);
    const agents = useMemo(() => agentsByBrand(assistants), [assistants]);
    const q = searchTerm.toLowerCase();
    const filtered = q
        ? list.filter((brand) => (brand.name || '').toLowerCase().includes(q) || (brand.description || '').toLowerCase().includes(q))
        : list;

    const emailModalBrand = emailModal ? list.find((b) => b.id === emailModal.brandId) : null;
    const availableEmails = emailModalBrand
        ? [userEmails.gmail_email, ...(userEmails.outlook_emails || [])]
            .filter((email) => email && !brandEmails(emailModalBrand).some((e) => e.email === email))
        : [];

    const setSearch = (value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set('q', value); else next.delete('q');
        setSearchParams(next, { replace: true });
    };

    // ------------------------------------------------------------ zones

    const subtitle = loading
        ? t('brandsPage.subtitle', 'Chaque marque relie ses boîtes mail entrantes, son API de commandes et son formulaire de ticket.')
        : summary.total === 0
            ? t('dataToolsUi.brands.subtitleEmpty', 'Une marque regroupe ses boîtes mail, son API de commandes, sa page support et ses agents.')
            : t('dataToolsUi.brands.subtitle', '{{total}} marques · {{mailboxes}} boîtes mail associées · {{api}} API de suivi actives', {
                total: formatCount(summary.total), mailboxes: formatCount(summary.mailboxes), api: formatCount(summary.apiOn),
            });

    const renderAttention = () => {
        if (!attention.length) return null;
        return (
            <Card icon={TriangleAlert} iconTone="warning" title={t('dataToolsUi.attention.title', 'À vérifier')}
                badge={<Badge tone="warning">{attention.length}</Badge>} flush className="bd-attention">
                <ul className="u-list">
                    {attention.map((item) => (
                        <li className="u-row" key={item.key}>
                            <Badge tone={item.tone} dot>
                                {item.tone === 'danger' ? t('dataToolsUi.attention.blocking', 'Bloquant') : t('dataToolsUi.attention.watch', 'À surveiller')}
                            </Badge>
                            <div className="u-row__main">
                                <div className="u-row__title">
                                    {item.kind === 'missing_key'
                                        ? t('dataToolsUi.brands.missingKey', 'L’API de suivi de « {{name}} » n’a pas de clé', { name: item.brand.name })
                                        : t('dataToolsUi.brands.partsKey', 'Outils pièces détachées désactivés pour « {{name}} »', { name: item.brand.name })}
                                </div>
                                <div className="u-row__meta">
                                    {item.kind === 'missing_key'
                                        ? t('brandsPage.apiKeyMissing', 'Clé API manquante — les appels de suivi échoueront.')
                                        : t('brandsPage.apiPartsKeyMissing', 'Aucune clé d’accès configurée — les outils pièces restent désactivés. Contactez Unanimity.')}
                                </div>
                            </div>
                            {item.kind === 'missing_key' && canManage && (
                                <div className="u-row__side">
                                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openModal(item.brand)}>
                                        {t('dataToolsUi.brands.addKey', 'Ajouter la clé')} <ChevronRight aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </Card>
        );
    };

    const renderKpis = () => {
        if (loading) {
            return (
                <div className="u-kpi-row bd-kpis" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton bd-kpi-skeleton" />)}
                </div>
            );
        }
        if (!summary.total) return null;
        return (
            <div className="u-kpi-row bd-kpis">
                <Kpi icon={Tags} label={t('dataToolsUi.brands.kpiBrands', 'Marques')} value={formatCount(summary.total)}
                    context={t('dataToolsUi.brands.kpiBrandsContext', '{{count}} avec une page support', { count: summary.withSupportPage })} />
                <Kpi icon={Mail} tone={summary.withoutMailbox ? 'warning' : 'success'} label={t('dataToolsUi.brands.kpiMailboxes', 'Boîtes mail associées')}
                    value={formatCount(summary.mailboxes)}
                    context={summary.withoutMailbox
                        ? t('dataToolsUi.brands.kpiMailboxesNone', '{{count}} marques sans boîte · {{vip}} VIP', { count: summary.withoutMailbox, vip: summary.vip })
                        : t('dataToolsUi.brands.kpiMailboxesVip', 'dont {{count}} VIP', { count: summary.vip })} />
                <Kpi icon={Plug} tone={summary.apiMissingKey ? 'danger' : summary.apiOn ? 'success' : 'neutral'} label={t('dataToolsUi.brands.kpiApi', 'API de suivi actives')}
                    value={formatCount(summary.apiOn)} unit={`/ ${formatCount(summary.total)}`}
                    context={summary.apiMissingKey
                        ? t('dataToolsUi.brands.kpiApiMissing', '{{count}} sans clé : appels refusés', { count: summary.apiMissingKey })
                        : t('dataToolsUi.brands.kpiApiShip24', '{{count}} suivies par Ship24 seul', { count: summary.total - summary.apiOn })}
                    contextTone={summary.apiMissingKey ? 'danger' : undefined}
                    meter={{ value: summary.apiOn, max: summary.total, tone: summary.apiMissingKey ? 'danger' : 'success', label: t('dataToolsUi.brands.kpiApi', 'API de suivi actives') }} />
                {summary.withAgent !== null && (
                    <Kpi icon={Bot} label={t('dataToolsUi.brands.kpiAgents', 'Avec un agent IA')} value={formatCount(summary.withAgent)} unit={`/ ${formatCount(summary.total)}`}
                        context={t('dataToolsUi.brands.kpiAgentsContext', 'les autres sont servies par les agents sans marque')}
                        meter={{ value: summary.withAgent, max: summary.total, label: t('dataToolsUi.brands.kpiAgents', 'Avec un agent IA') }} />
                )}
            </div>
        );
    };

    const apiBadge = (brand) => {
        const state = brandApiState(brand);
        if (state === 'ok') return <Badge tone="success" dot>{t('dataToolsUi.brands.apiOn', 'API active')}</Badge>;
        if (state === 'missing_key') return <Badge tone="danger" dot>{t('dataToolsUi.brands.apiMissingKey', 'Clé API manquante')}</Badge>;
        if (state === 'disabled') return <Badge>{t('dataToolsUi.brands.apiDisabled', 'Ship24 seul')}</Badge>;
        return null;
    };

    const renderApi = (brand) => {
        const config = brand.tracking_api_config;
        const state = brandApiState(brand);
        if (!config) return <p className="bd-muted">{t('brandsPage.noApi', 'Non configurée.')}</p>;
        if (state === 'disabled') return <p className="bd-muted">{t('brandsPage.apiDisabled', 'Désactivée — suivi via Ship24 uniquement.')}</p>;
        const fields = apiFieldsOf(config);
        const configured = fields.filter((f) => (f.isKey ? config.has_api_key : config[f.key]));
        return (
            <>
                <p className={state === 'missing_key' ? 'bd-api-line bd-api-line--danger' : 'bd-api-line'}>
                    {state === 'missing_key'
                        ? t('brandsPage.apiKeyMissing', 'Clé API manquante — les appels de suivi échoueront.')
                        : state === 'ok'
                            ? t('brandsPage.apiConfigured', 'Configurée ({{total}} champs)', { total: configured.length })
                            : t('brandsPage.noApi', 'Non configurée.')}
                </p>
                <details className="u-disclosure bd-details">
                    <summary><ChevronRight aria-hidden="true" />{t('brandsPage.apiDetails', 'Détails')}</summary>
                    <DescriptionList items={fields.map((field) => {
                        const value = field.isKey ? config.has_api_key : config[field.key];
                        return {
                            label: field.label,
                            value: value
                                ? (field.isKey ? t('brandsPage.apiKeySet', 'renseignée') : <span className="bd-url" title={value}>{value}</span>)
                                : '—',
                        };
                    })} />
                </details>
            </>
        );
    };

    const renderBrand = (brand) => {
        const emails = brandEmails(brand);
        const created = formatDay(brand.created_at);
        const updated = formatDay(brand.updated_at);
        const support = webHref(brand.support_page_url);
        const brandAgents = agents[brand.id] || [];
        return (
            <Card
                key={brand.id}
                icon={Tag}
                className="bd-card"
                title={brand.name}
                badge={apiBadge(brand)}
                description={brand.description}
                actions={canManage ? (
                    <>
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openModal(brand)}>
                            <Pencil aria-hidden="true" /> {t('brandsPage.edit', 'Modifier')}
                        </button>
                        <OverflowMenu label={t('dataToolsUi.brands.more', 'Plus d’actions pour {{name}}', { name: brand.name })} items={[
                            { label: t('brandsPage.copyEmbed', 'Copier le formulaire de ticket'), icon: CodeXml, onClick: () => copyTicketEmbed(brand) },
                            { label: t('brandsPage.associateEmail', 'Associer une boîte mail'), icon: Mail, onClick: () => setEmailModal({ brandId: brand.id, pendingEmail: null, vip: false, loading: false, error: null }) },
                            { separator: true },
                            { label: t('brandsPage.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setDeleteModal({ brand, deleting: false, error: null }) },
                        ]} />
                    </>
                ) : null}
                footerNote={[
                    created && t('brandsPage.metaCreated', 'Créée le {{date}}', { date: created }),
                    updated && updated !== created && t('brandsPage.metaUpdated', 'modifiée le {{date}}', { date: updated }),
                ].filter(Boolean).join(' · ')}
            >
                <div className="bd-sections">
                    <section className="bd-section">
                        <h3 className="u-overline">{t('brandsPage.mailboxes', 'Boîtes mail associées')}</h3>
                        {emails.length === 0 ? (
                            <p className="bd-muted">{t('brandsPage.noMailboxes', 'Aucune boîte mail — les courriels entrants ne sont pas rattachés à cette marque.')}</p>
                        ) : (
                            <ul className="bd-emails">
                                {emails.map((emailObj) => (
                                    <li key={emailObj.email} className="bd-email">
                                        <Mail className="bd-email__icon" aria-hidden="true" />
                                        <span className="bd-email__text" title={emailObj.email}>{emailObj.email}</span>
                                        {emailObj.is_vip && <Badge tone="warning" icon={Star}>VIP</Badge>}
                                        {canManage && !emailObj.legacy && (
                                            <span className="bd-email__actions">
                                                <button type="button" className={`u-btn u-btn--ghost u-btn--icon u-btn--sm bd-vip${emailObj.is_vip ? ' is-on' : ''}`}
                                                    onClick={() => toggleVip(brand.id, emailObj.email, emailObj.is_vip)}
                                                    title={emailObj.is_vip ? t('brandsPage.removeVip', 'Retirer le statut VIP') : t('brandsPage.makeVip', 'Marquer VIP')}
                                                    aria-label={emailObj.is_vip ? t('brandsPage.removeVip', 'Retirer le statut VIP') : t('brandsPage.makeVip', 'Marquer VIP')}
                                                    aria-pressed={Boolean(emailObj.is_vip)}>
                                                    <Star aria-hidden="true" />
                                                </button>
                                                <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                                                    onClick={() => setRemoveEmailConfirm({ brand, email: emailObj.email })}
                                                    title={t('brandsPage.removeEmail', 'Retirer cette boîte mail')} aria-label={t('brandsPage.removeEmail', 'Retirer cette boîte mail')}>
                                                    <X aria-hidden="true" />
                                                </button>
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {canManage && (
                            <button type="button" className="u-btn u-btn--ghost u-btn--sm bd-add"
                                onClick={() => setEmailModal({ brandId: brand.id, pendingEmail: null, vip: false, loading: false, error: null })}>
                                <Plus aria-hidden="true" /> {t('brandsPage.associateEmail', 'Associer une boîte mail')}
                            </button>
                        )}
                    </section>

                    <section className="bd-section">
                        <h3 className="u-overline">{t('brandsPage.trackingApi', 'API de suivi')}</h3>
                        {renderApi(brand)}
                    </section>

                    {assistants && (
                        <section className="bd-section">
                            <h3 className="u-overline">{t('dataToolsUi.brands.agents', 'Agents IA de la marque')}</h3>
                            {brandAgents.length ? (
                                <ul className="bd-agents">
                                    {brandAgents.map((agent) => (
                                        <li key={agent._id}>
                                            <Link className="bd-agent" to={`/ai-assistants?assistantId=${encodeURIComponent(agent._id)}`}>
                                                <Bot aria-hidden="true" /> {agent.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="bd-muted">{t('dataToolsUi.brands.noAgent', 'Aucun agent dédié : les agents sans marque répondent.')}</p>
                            )}
                        </section>
                    )}

                    <section className="bd-section">
                        <h3 className="u-overline">{t('brandsPage.supportPageUrl', 'Page support')}</h3>
                        {support ? (
                            <a className="u-link bd-support" href={support} target="_blank" rel="noopener noreferrer" title={support}>
                                <span className="bd-url">{support}</span> <ExternalLink aria-hidden="true" />
                            </a>
                        ) : brand.support_page_url ? (
                            <span className="bd-url">{brand.support_page_url}</span>
                        ) : (
                            <p className="bd-muted">{t('dataToolsUi.brands.noSupport', 'Aucune : les notifications et le chatbot n’envoient pas de lien.')}</p>
                        )}
                    </section>
                </div>
            </Card>
        );
    };

    const renderList = () => {
        if (loading) {
            return (
                <div className="bd-grid" aria-busy="true" aria-label={t('common.loading', 'Chargement…')}>
                    {[0, 1].map((i) => <span key={i} className="u-skeleton bd-card-skeleton" />)}
                </div>
            );
        }
        if (list.length === 0) {
            if (loadError) return null;
            return (
                <Card>
                    <EmptyState icon={Tags} title={t('dataToolsUi.brands.emptyTitle', 'Aucune marque pour le moment')}
                        action={canManage ? (
                            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => openModal()}>
                                <Plus aria-hidden="true" /> {t('brandsPage.create', 'Créer une marque')}
                            </button>
                        ) : null}>
                        {t('dataToolsUi.brands.emptyHint', 'Créez une marque pour rattacher ses boîtes mail, son API de commandes, sa page support et ses agents.')}
                    </EmptyState>
                </Card>
            );
        }
        if (filtered.length === 0) {
            return (
                <Card>
                    <EmptyState icon={Search} title={t('brandsPage.noMatches', 'Aucune marque ne correspond à la recherche.')}
                        action={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setSearch('')}>{t('dataToolsUi.clearSearch', 'Effacer la recherche')}</button>} />
                </Card>
            );
        }
        return <div className="bd-grid">{filtered.map(renderBrand)}</div>;
    };

    const editingConfig = modal?.editing?.tracking_api_config;

    return (
        <div className="u-page bd-page">
            <PageHeader
                title={t('brandsPage.title', 'Marques')}
                subtitle={subtitle}
                actions={canManage ? (
                    <button type="button" className="u-btn u-btn--primary" onClick={() => openModal()}>
                        <Plus aria-hidden="true" /> {t('brandsPage.create', 'Créer une marque')}
                    </button>
                ) : null}
            />

            <div className="u-well">
                {loadError && (
                    <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchBrands}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>
                        {loadError}
                    </Banner>
                )}
                {renderAttention()}
                {renderKpis()}
                {list.length > 0 && (
                    <div className="bd-toolbar">
                        <div className="u-input-group u-input-group--lead bd-search">
                            <Search className="u-input-group__icon" aria-hidden="true" />
                            <input type="search" className="u-input" value={searchTerm} onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('brandsPage.searchPlaceholder', 'Rechercher une marque…')} aria-label={t('brandsPage.searchPlaceholder', 'Rechercher une marque…')} />
                        </div>
                        <span className="bd-count">{t('dataToolsUi.brands.shown', '{{count}} affichées', { count: formatCount(filtered.length) })}</span>
                    </div>
                )}
                {renderList()}
            </div>

            {/* Create / edit */}
            {modal && (
                <Modal
                    wide
                    icon={Tag}
                    title={modal.editing ? t('brandsPage.editTitle', 'Modifier la marque') : t('brandsPage.createTitle', 'Créer une marque')}
                    description={modal.editing ? modal.editing.name : t('dataToolsUi.brands.createDesc', 'Le nom et la description suffisent ; l’API et la page support s’ajoutent quand vous voulez.')}
                    onClose={() => !modal.saving && setModal(null)}
                    closeLabel={t('common.close', 'Fermer')}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setModal(null)} disabled={modal.saving}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="submit" form="bd-form" className="u-btn u-btn--primary" disabled={modal.saving}>
                                {modal.saving
                                    ? t('common.saving', 'Enregistrement…')
                                    : modal.editing ? t('common.save', 'Enregistrer') : t('brandsPage.submitCreate', 'Créer la marque')}
                            </button>
                        </>
                    )}
                >
                    <form id="bd-form" className="u-form" onSubmit={submitModal} noValidate>
                        {modal.error && <Banner tone="danger">{modal.error}</Banner>}
                        <fieldset className="bd-fieldset">
                            <legend className="u-overline">{t('brandsPage.sectionIdentity', 'Identité')}</legend>
                            <Field label={`${t('brandsPage.name', 'Nom')} *`} htmlFor="bd-name">
                                <input id="bd-name" className="u-input" type="text" maxLength={100} value={modal.form.name} onChange={(e) => setForm({ name: e.target.value })} />
                            </Field>
                            <Field label={`${t('brandsPage.description', 'Description')} *`} htmlFor="bd-desc"
                                help={t('brandsPage.descriptionPlaceholder', 'Utilisée par l’IA pour identifier la marque…')}>
                                <textarea id="bd-desc" className="u-textarea" rows={3} maxLength={500} value={modal.form.description} onChange={(e) => setForm({ description: e.target.value })} />
                            </Field>
                        </fieldset>

                        <fieldset className="bd-fieldset">
                            <legend className="u-overline">{t('brandsPage.sectionApi', 'API de suivi')}</legend>
                            <p className="u-help bd-help">{t('brandsPage.sectionApiHint', 'Optionnelle — permet à l’IA de consulter commandes et suivis de cette marque.')}</p>
                            <Switch id="bd-api-enabled" checked={modal.form.tracking_api_config.enabled !== false} onChange={(on) => setApiForm({ enabled: on })}
                                label={t('brandsPage.apiEnabled', 'API interne activée (décochée : suivi via Ship24 uniquement)')} />
                            <div className="u-form-grid">
                                <Field label={t('brandsPage.apiOrdersUrl', 'URL des commandes')} htmlFor="bd-orders-url">
                                    <input id="bd-orders-url" className="u-input" type="url" value={modal.form.tracking_api_config.orders_get_url}
                                        onChange={(e) => setApiForm({ orders_get_url: e.target.value })} placeholder="https://api.exemple.com/orders/{num_commande}" />
                                </Field>
                                <Field label={t('brandsPage.apiTrackingUrl', 'URL de suivi')} htmlFor="bd-tracking-url">
                                    <input id="bd-tracking-url" className="u-input" type="url" value={modal.form.tracking_api_config.orders_tracking_url}
                                        onChange={(e) => setApiForm({ orders_tracking_url: e.target.value })} placeholder="https://api.exemple.com/tracking" />
                                </Field>
                                <Field label={t('brandsPage.apiStockUrl', 'URL du stock')} htmlFor="bd-stock-url"
                                    help={t('brandsPage.apiStockUrlHint', 'Optionnelle — délai de disponibilité des articles d’une commande, consulté par l’agent IA avec la clé API ci-dessous.')}>
                                    <input id="bd-stock-url" className="u-input" type="url" value={modal.form.tracking_api_config.stock_url}
                                        onChange={(e) => setApiForm({ stock_url: e.target.value })} placeholder="https://api.exemple.com/skus/stock" />
                                </Field>
                                <Field label={t('brandsPage.apiKey', 'Clé API')} htmlFor="bd-api-key"
                                    help={editingConfig?.has_api_key
                                        ? t('dataToolsUi.brands.keyStored', 'Une clé est enregistrée ; elle n’est jamais réaffichée. Laissez vide pour la garder.')
                                        : t('dataToolsUi.secret.help', 'Enregistré pour les lectures, jamais réaffiché.')}>
                                    <input id="bd-api-key" className="u-input" type="password" autoComplete="new-password" value={modal.form.tracking_api_config.api_key}
                                        onChange={(e) => setApiForm({ api_key: e.target.value })}
                                        placeholder={editingConfig?.has_api_key
                                            ? t('brandsPage.apiKeyKeep', 'Laisser vide pour conserver la clé actuelle')
                                            : t('brandsPage.apiKeyPlaceholder', 'Jeton d’authentification')} />
                                </Field>
                            </div>
                            {hasPartsSource(editingConfig) && (
                                <Field label={t('brandsPage.apiPartsUrl', 'URL des pièces détachées')} htmlFor="bd-parts-url"
                                    help={`${t('brandsPage.apiPartsUrlHint', 'Catalogue de pièces détachées consulté par l’agent IA (disponibilité, compatibilité).')} ${editingConfig.has_parts_api_key
                                        ? t('brandsPage.apiPartsKeySet', 'Clé d’accès configurée par Unanimity.')
                                        : t('brandsPage.apiPartsKeyMissing', 'Aucune clé d’accès configurée — les outils pièces restent désactivés. Contactez Unanimity.')}`}>
                                    <input id="bd-parts-url" className="u-input" type="url" value={modal.form.tracking_api_config.parts_api_url}
                                        onChange={(e) => setApiForm({ parts_api_url: e.target.value })} placeholder="https://api.exemple.com/parts" />
                                </Field>
                            )}
                        </fieldset>

                        <fieldset className="bd-fieldset">
                            <legend className="u-overline">{t('brandsPage.sectionSupport', 'Support client')}</legend>
                            <Field label={t('brandsPage.supportPageUrl', 'Page support')} htmlFor="bd-support-url"
                                help={t('brandsPage.supportPageUrlHint', 'Page de votre site où le formulaire de ticket est intégré : les e-mails de notification et le chatbot y renvoient le client. Vide : aucun lien n’est envoyé.')}>
                                <input id="bd-support-url" className="u-input" type="url" maxLength={2048} value={modal.form.support_page_url}
                                    onChange={(e) => setForm({ support_page_url: e.target.value })} placeholder="https://www.exemple.com/support" />
                            </Field>
                        </fieldset>
                    </form>
                </Modal>
            )}

            {deleteModal && (
                <ConfirmDialog
                    title={t('dataToolsUi.brands.deleteTitle', 'Supprimer la marque « {{name}} » ?', { name: deleteModal.brand.name })}
                    message={t('brandsPage.deleteConfirm', 'Supprimer « {{name}} » ? Les associations de boîtes mail et la configuration API seront perdues.', { name: deleteModal.brand.name })}
                    confirmLabel={t('dataToolsUi.brands.deleteConfirm', 'Supprimer la marque')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={deleteModal.deleting}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteModal(null)}
                />
            )}

            {removeEmailConfirm && (
                <ConfirmDialog
                    title={t('dataToolsUi.brands.removeEmailTitle', 'Retirer {{email}} de « {{name}} » ?', { email: removeEmailConfirm.email, name: removeEmailConfirm.brand.name })}
                    message={t('dataToolsUi.brands.removeEmailMessage', 'Les e-mails reçus sur cette boîte ne seront plus rattachés à la marque. Les tickets existants ne changent pas.')}
                    confirmLabel={t('brandsPage.removeEmail', 'Retirer cette boîte mail')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    onConfirm={() => removeEmail(removeEmailConfirm.brand.id, removeEmailConfirm.email)}
                    onCancel={() => setRemoveEmailConfirm(null)}
                />
            )}

            {/* Associate a mailbox */}
            {emailModal && emailModalBrand && (
                <Modal
                    icon={Mail}
                    title={t('brandsPage.emailModalTitle', 'Boîtes mail — {{name}}', { name: emailModalBrand.name })}
                    description={t('dataToolsUi.brands.emailModalDesc', 'Les e-mails reçus sur la boîte choisie seront rattachés à cette marque.')}
                    onClose={() => !emailModal.loading && setEmailModal(null)}
                    closeLabel={t('common.close', 'Fermer')}
                    footer={availableEmails.length ? (
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setEmailModal(null)} disabled={emailModal.loading}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary" disabled={emailModal.loading || !emailModal.pendingEmail}
                                onClick={() => associateEmail(emailModal.brandId, emailModal.pendingEmail, emailModal.vip)}>
                                {emailModal.loading ? t('brandsPage.adding', 'Association…') : t('brandsPage.addEmail', 'Associer')}
                            </button>
                        </>
                    ) : null}
                >
                    <div className="u-form">
                        {emailModal.error && <Banner tone="danger">{emailModal.error}</Banner>}
                        {availableEmails.length === 0 ? (
                            <EmptyState icon={Mail} title={t('dataToolsUi.brands.noMoreTitle', 'Aucune boîte disponible')}>
                                {t('brandsPage.noMoreEmails', 'Toutes vos boîtes connectées sont déjà associées à cette marque.')}
                            </EmptyState>
                        ) : (
                            <>
                                <span className="u-label">{t('brandsPage.availableEmails', 'Boîtes connectées disponibles')}</span>
                                <div className="bd-options" role="radiogroup" aria-label={t('brandsPage.availableEmails', 'Boîtes connectées disponibles')}>
                                    {availableEmails.map((email) => (
                                        <button key={email} type="button" role="radio" aria-checked={emailModal.pendingEmail === email}
                                            className={`bd-option${emailModal.pendingEmail === email ? ' is-selected' : ''}`}
                                            onClick={() => setEmailModal((m) => ({ ...m, pendingEmail: email }))} disabled={emailModal.loading}>
                                            <Mail aria-hidden="true" /> <span className="bd-option__text">{email}</span>
                                        </button>
                                    ))}
                                </div>
                                <Switch id="bd-vip" checked={emailModal.vip} onChange={(on) => setEmailModal((m) => ({ ...m, vip: on }))}
                                    label={t('brandsPage.markAsVip', 'Marquer comme adresse VIP')} />
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {toast && (
                <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>{toast.text}</Toast>
            )}
        </div>
    );
};

export default Brands;
